import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { openInMobileApp, isMobileDevice } from '../utils/mobileApp';

interface AppStorePopupContextType {
  isOpen: boolean;
  openAppStorePopup: (options?: { itmSource?: string }) => Promise<void>;
  closeAppStorePopup: () => void;
}

const AppStorePopupContext = createContext<AppStorePopupContextType | undefined>(undefined);

interface AppStorePopupProviderProps {
  children: ReactNode;
}

export const AppStorePopupProvider: React.FC<AppStorePopupProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);

  const openAppStorePopup = useCallback(async (options?: { itmSource?: string }) => {
    if (isMobileDevice()) {
      try {
        const appOpened = await openInMobileApp({ itmSource: options?.itmSource || 'download_app' });
        
        if (appOpened) {
          // App opened successfully, ensure modal is closed
          setIsOpen(false);
        } else {
          // App didn't open, show modal
          setIsOpen(true);
        }
      } catch (error) {
        setIsOpen(true);
      }
    } else {
      // Desktop - show modal immediately
      setIsOpen(true);
    }
  }, []);

  const closeAppStorePopup = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value: AppStorePopupContextType = {
    isOpen,
    openAppStorePopup,
    closeAppStorePopup,
  };

  return (
    <AppStorePopupContext.Provider value={value}>
      {children}
    </AppStorePopupContext.Provider>
  );
};

export const useAppStorePopup = (): AppStorePopupContextType => {
  const context = useContext(AppStorePopupContext);
  if (context === undefined) {
    throw new Error('useAppStorePopup must be used within an AppStorePopupProvider');
  }
  return context;
};
